import React from "react";
import SubPageLayout from "../components/SubPageLayout";
import { motion } from "framer-motion";
import {
  NAAVI_STORY,
  NAAVI_VISION,
  NAAVI_MISSION,
  NAAVI_TEAM,
  NAAVI_BOARD,
} from "../data/aboutContent";
import { Quote, Sparkles } from "lucide-react";

export default function About() {
  return (
    <SubPageLayout
      eyebrow="About"
      title={
        <>
          From vision to reality —<br />
          <span className="italic font-normal">the Naavi journey</span>.
        </>
      }
      subtitle="Our story, our purpose and the people building Naavi Early Years for tomorrow's young thinkers."
      accent="lime"
      decor="square"
    >
      {/* THE NAAVI STORY */}
      <section
        id="story"
        className="py-14 md:py-24 bg-[#FDFDF9] scroll-mt-32"
        data-testid="about-story"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-4 block">
                Our Story
              </span>
              <h2 className="font-display text-[32px] md:text-5xl text-[#0F1B47] font-semibold leading-tight">
                {NAAVI_STORY.title}
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6 text-[#4A5568] text-base md:text-lg leading-relaxed font-light">
              {NAAVI_STORY.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}

              {/* Pullquote */}
              <div className="relative bg-[#F5F5F0] border-l-4 border-[#C6D947] rounded-r-3xl px-7 py-6 mt-2">
                <Quote size={22} className="text-[#648DE5] mb-3" />
                <p className="font-display italic text-[#0F1B47] text-xl md:text-2xl leading-snug">
                  {NAAVI_STORY.pullquote}
                </p>
              </div>
            </div>
          </div>

          {/* Story image strip */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
            {NAAVI_STORY.images.map((url, i) => (
              <motion.div
                key={url}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="aspect-[4/3] overflow-hidden rounded-3xl"
              >
                <img decoding="async"
                  src={url}
                  alt={`Naavi moment ${i + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISION */}
      <section
        id="vision"
        className="py-14 md:py-24 bg-[#F5F5F0] scroll-mt-32"
        data-testid="about-vision"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="rounded-[36px] overflow-hidden aspect-[4/5] shadow-[0_30px_80px_-30px_rgba(15,27,71,0.25)]">
                <img decoding="async" loading="lazy"
                  src={NAAVI_VISION.image}
                  alt="Children at Naavi Early Years"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-6">
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-4 block">
                Vision
              </span>
              <h2 className="font-display text-[32px] md:text-5xl lg:text-6xl text-[#0F1B47] font-semibold leading-tight mb-6">
                {NAAVI_VISION.heading}
              </h2>
              <p className="text-[#4A5568] text-lg md:text-xl leading-relaxed font-light max-w-xl">
                {NAAVI_VISION.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section
        id="mission"
        className="py-14 md:py-24 bg-[#FDFDF9] scroll-mt-32"
        data-testid="about-mission"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl mb-14">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-4 block">
              Mission
            </span>
            <h2 className="font-display text-[32px] md:text-5xl text-[#0F1B47] font-semibold leading-tight mb-5">
              {NAAVI_MISSION.heading}
            </h2>
            <p className="text-[#4A5568] text-base md:text-lg leading-relaxed font-light">
              {NAAVI_MISSION.intro}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {NAAVI_MISSION.items.map((m, i) => (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bento-card bg-[#F5F5F0] rounded-3xl p-7 border border-[#0F1B47]/6"
                data-testid={`mission-card-${i}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-full bg-[#FDFDF9] border border-[#0F1B47]/8 flex items-center justify-center text-[#648DE5]">
                    <Sparkles size={15} />
                  </div>
                  <span className="font-display text-2xl text-[#648DE5] font-semibold">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-lg text-[#0F1B47] font-medium mb-2 leading-snug">
                  {m.title}
                </h3>
                <p className="text-[#4A5568] text-sm leading-relaxed font-light">
                  {m.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR TEAM */}
      <section
        id="team"
        className="py-14 md:py-24 bg-[#F5F5F0] scroll-mt-32"
        data-testid="about-team"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-4 block">
              Our Team
            </span>
            <h2 className="font-display text-[32px] md:text-5xl text-[#0F1B47] font-semibold leading-tight">
              The people behind <span className="italic font-normal">Naavi</span>.
            </h2>
          </div>

          <div className="space-y-10">
            {NAAVI_TEAM.map((m, i) => (
              <PersonCard key={m.name} person={m} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* BOARD OF TRUSTEES */}
      <section
        id="board"
        className="py-14 md:py-24 bg-[#FDFDF9] scroll-mt-32"
        data-testid="about-board"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-4 block">
              Board of Trustees
            </span>
            <h2 className="font-display text-[32px] md:text-5xl text-[#0F1B47] font-semibold leading-tight">
              Stewards of our <span className="italic font-normal">vision</span>.
            </h2>
          </div>

          <div className="space-y-10">
            {NAAVI_BOARD.map((m, i) => (
              <PersonCard key={m.name} person={m} index={i} />
            ))}
          </div>
        </div>
      </section>
    </SubPageLayout>
  );
}

/**
 * Person card with portrait-style photo (matches the source pages).
 * Image renders at ~300px wide on the left column on desktop.
 */
function PersonCard({ person, index }) {
  const reverse = index % 2 === 1;
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6 }}
      className={`bg-[#FDFDF9] rounded-[32px] border border-[#0F1B47]/8 overflow-hidden grid md:grid-cols-12 gap-0 ${
        reverse ? "md:[&>.image]:order-2" : ""
      }`}
      data-testid={`person-${person.name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="image md:col-span-4 lg:col-span-3 bg-[#F5F5F0] flex items-start justify-center p-6 md:p-8">
        <div className="w-full max-w-[300px] aspect-square overflow-hidden rounded-3xl">
          <img decoding="async"
            src={person.image}
            alt={person.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
      <div className="md:col-span-8 lg:col-span-9 p-7 md:p-10">
        <h3 className="font-display text-3xl md:text-4xl text-[#0F1B47] font-semibold leading-tight mb-2">
          {person.name}
        </h3>
        <div className="text-sm text-[#4A5568] font-light italic mb-2">
          {person.qualification}
        </div>
        <div className="inline-block bg-[#0F1B47] text-[#FDFDF9] text-[11px] font-bold tracking-[0.18em] uppercase rounded-full px-3 py-1.5 mb-5">
          {person.role}
        </div>
        <div className="space-y-4 text-[#4A5568] text-[15px] leading-relaxed font-light">
          {person.bio.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      </div>
    </motion.article>
  );
}
